import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMeetingState, type MeetingAgendaItem } from '../context/MeetingStateContext';
import { useLiveInsights } from '../context/LiveInsightsContext';
import { decodeHtmlEntities } from '../utils/displayText';
import api from '../services/api';
import PersonalQueueList from './PersonalQueueList';
import ViewerToneChip from './ViewerToneChip';

const HIDDEN_PRIVATE_NOTIFICATION_CODES = new Set([
  'time_warning_while_speaking',
  'wrapup_while_speaking',
  'overtime_while_speaking',
]);

type CommandState = 'idle' | 'sending' | 'sent' | 'error';

/** Format a timestamp as relative time (e.g. "2m ago") */
function formatRelativeTime(ms: number): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}

/** Format seconds into a human-readable string. Shows seconds when under 2 minutes. */
function formatTimeValue(sec: number, suffix: string): string {
  if (sec < 120) return `${sec}s ${suffix}`;
  return `${Math.ceil(sec / 60)}m ${suffix}`;
}

function LiveControlsPanel() {
  const { meetingState, getMeetingId } = useMeetingState();
  const { personalView, isLoading: insightsLoading, postPersonalAction } = useLiveInsights();
  const [commandStates, setCommandStates] = useState<Record<string, CommandState>>({});
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const meetingId = getMeetingId();
  const lifecycleStatus = meetingState?.lifecycle?.status ?? 'created';
  const isMeetingActive = lifecycleStatus === 'live';
  const agendaItems: MeetingAgendaItem[] = meetingState?.agenda?.items ?? [];
  const activeAgendaItemId = meetingState?.agenda?.activeAgendaItemId ?? null;

  const activeItem = activeAgendaItemId
    ? agendaItems.find((i) => i.agendaItemId === activeAgendaItemId)
    : null;
  const activeIdx = activeItem
    ? agendaItems.indexOf(activeItem)
    : -1;
  const isLastItem = activeIdx >= 0 && activeIdx === agendaItems.length - 1;
  const hasNextItem = activeIdx >= 0 && activeIdx < agendaItems.length - 1;
  const nextItem = hasNextItem ? agendaItems[activeIdx + 1] : null;

  // Client-side timer interpolation: count down locally between server polls
  const serverRemainingSec = activeItem?.timebox?.remainingSec ?? null;
  const serverOvertimeSec = activeItem?.timebox?.overtimeSec ?? null;
  const isServerOvertime = serverOvertimeSec != null && serverOvertimeSec > 0;
  const [displaySec, setDisplaySec] = useState<number | null>(
    isServerOvertime ? (serverOvertimeSec ?? null) : (serverRemainingSec ?? null),
  );
  const [displayOvertime, setDisplayOvertime] = useState(isServerOvertime);
  const lastServerUpdateRef = useRef<number>(Date.now());

  useEffect(() => {
    setDisplayOvertime(isServerOvertime);
    setDisplaySec(isServerOvertime ? (serverOvertimeSec ?? null) : (serverRemainingSec ?? null));
    lastServerUpdateRef.current = Date.now();
  }, [serverRemainingSec, serverOvertimeSec, isServerOvertime]);

  useEffect(() => {
    if (serverRemainingSec === null || !isMeetingActive) return;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastServerUpdateRef.current) / 1000);
      if (isServerOvertime) {
        // Counting up during overtime
        setDisplaySec((serverOvertimeSec ?? 0) + elapsed);
      } else {
        const remaining = (serverRemainingSec ?? 0) - elapsed;
        if (remaining <= 0) {
          setDisplayOvertime(true);
          setDisplaySec(Math.abs(remaining));
        } else {
          setDisplaySec(remaining);
        }
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [serverRemainingSec, serverOvertimeSec, isServerOvertime, isMeetingActive]);

  const isOvertime = displayOvertime;

  const sendCommand = useCallback(async (key: string, command: string) => {
    if (!meetingId) return;
    setCommandStates((prev) => ({ ...prev, [key]: 'sending' }));
    try {
      await api.sendCommand(meetingId, command);
      setCommandStates((prev) => ({ ...prev, [key]: 'sent' }));
      setTimeout(() => {
        setCommandStates((prev) => ({ ...prev, [key]: 'idle' }));
      }, 2000);
    } catch {
      setCommandStates((prev) => ({ ...prev, [key]: 'error' }));
      setTimeout(() => {
        setCommandStates((prev) => ({ ...prev, [key]: 'idle' }));
      }, 3000);
    }
  }, [meetingId]);

  const btnLabel = (key: string, defaultLabel: string) => {
    const state = commandStates[key];
    if (state === 'sending') return 'Sending...';
    if (state === 'sent') return 'Done';
    if (state === 'error') return 'Failed — tap to retry';
    return defaultLabel;
  };

  const btnDisabled = (key: string) => {
    return commandStates[key] === 'sending' || commandStates[key] === 'sent';
  };

  const btnClass = (key: string, base: string) => {
    const state = commandStates[key];
    if (state === 'error') return `${base} lc-btn-error`;
    return base;
  };

  // Coaching data
  const visibleNotifications = personalView?.privateNotifications.filter(
    (n) => !HIDDEN_PRIVATE_NOTIFICATION_CODES.has(n.code),
  ) ?? [];
  const hasCoaching =
    personalView &&
    (personalView.overallToneSignal != null ||
      personalView.privateTips.length > 0 ||
      visibleNotifications.length > 0 ||
      personalView.personalQueue.some((i) => i.actionState.status === 'open'));

  if (!isMeetingActive) {
    return (
      <div className="lc-panel">
        <div className="lc-status">
          <div className="lc-status-icon">
            {lifecycleStatus === 'completed' || lifecycleStatus === 'ended' ? '✅' : '⏳'}
          </div>
          <div className="lc-status-text">
            {lifecycleStatus === 'completed' || lifecycleStatus === 'ended'
              ? 'Meeting ended'
              : 'Waiting for meeting to start...'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lc-panel">
      {/* Current item context */}
      {activeItem && (
        <div className="lc-current-item">
          <div className="lc-section-label">Current Item</div>
          <div className="lc-item-title">
            {activeIdx + 1}. {decodeHtmlEntities(activeItem.title)}
          </div>
          {displaySec !== null && (
            <div className={`lc-time-badge ${isOvertime ? 'lc-overtime' : ''}`}>
              {isOvertime
                ? formatTimeValue(displaySec, 'overtime')
                : formatTimeValue(displaySec, 'remaining')}
            </div>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="lc-section-label">Quick Actions</div>
      <div className="lc-actions">
        {hasNextItem && nextItem && (
          <button
            className={btnClass('next', 'lc-btn lc-btn-primary')}
            disabled={btnDisabled('next')}
            onClick={() => sendCommand('next', 'next item')}
          >
            {btnLabel('next', `Next: ${decodeHtmlEntities(nextItem.title)}`)}
          </button>
        )}
        <button
          className={btnClass('add5', 'lc-btn lc-btn-secondary')}
          disabled={btnDisabled('add5')}
          onClick={() => sendCommand('add5', 'add 5 minutes')}
        >
          {btnLabel('add5', '+5 Minutes')}
        </button>
        <button
          className={btnClass('add2', 'lc-btn lc-btn-secondary')}
          disabled={btnDisabled('add2')}
          onClick={() => sendCommand('add2', 'add 2 minutes')}
        >
          {btnLabel('add2', '+2 Minutes')}
        </button>
        {isLastItem && (
          <button
            className={btnClass('end', 'lc-btn lc-btn-end')}
            disabled={btnDisabled('end')}
            onClick={() => setShowEndConfirm(true)}
          >
            {btnLabel('end', 'End Meeting')}
          </button>
        )}
      </div>

      {/* Personalized coaching */}
      {insightsLoading && !personalView && (
        <div className="lc-coaching">
          <div className="lc-section-label">Your Coaching</div>
          <div className="lc-loading">Loading insights...</div>
        </div>
      )}
      {hasCoaching && (
        <div className="lc-coaching">
          <div className="lc-section-label">Your Coaching</div>
          {personalView!.overallToneSignal && (
            <ViewerToneChip tone={personalView!.overallToneSignal} />
          )}
          {personalView!.privateTips.length > 0 && (
            <div className="lc-tips">
              {personalView!.privateTips.map((tip, i) => (
                <div key={i} className="lc-tip">{tip}</div>
              ))}
            </div>
          )}
          {visibleNotifications.length > 0 && (
            <div className="lc-notifications">
              {visibleNotifications.map((n) => (
                <div
                  key={n.notificationId}
                  className={`lc-notification ${n.severity === 'alert' ? 'lc-alert' : 'lc-warning'}`}
                >
                  <div className="lc-notification-header">
                    <span className="lc-notification-title">{n.title}</span>
                    <span className="lc-notification-time">{formatRelativeTime(n.createdAtMs)}</span>
                  </div>
                  <span className="lc-notification-msg">{n.message}</span>
                </div>
              ))}
            </div>
          )}
          <PersonalQueueList
            items={personalView!.personalQueue}
            onAction={(itemId, action) => void postPersonalAction(itemId, action)}
          />
        </div>
      )}

      {/* End Meeting confirmation modal */}
      {showEndConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowEndConfirm(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-label="End meeting confirmation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <span className="text-red-600 text-lg">!</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">End this meeting?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will wrap up the last agenda item and trigger the meeting recap and scorecard.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button
                type="button"
                onClick={() => setShowEndConfirm(false)}
                className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => {
                  setShowEndConfirm(false);
                  sendCommand('end', 'end meeting');
                }}
                className="px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Yes, end meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveControlsPanel;
